import * as React from 'react'
import { Link } from 'react-router'
import { cn } from '@/lib/utils'
import {
  Combobox,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  useComboboxContext,
} from '@/components/ui/combobox'

export interface MultiSelectOption {
  label: string
  value: string
}

export interface MultiSelectProps {
  options: MultiSelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  selectionLimit?: number
  display?: 'chip' | 'comma'
  /** Makes each chip's label a link to the selected object. */
  href?: (value: string) => string
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className,
  disabled,
  selectionLimit,
  href,
}: MultiSelectProps) {
  const handleValuesChange = (newValue: string[]) => {
    if (selectionLimit && newValue.length > selectionLimit) return
    onChange(newValue)
  }

  const labelMap = React.useMemo(
    () => Object.fromEntries(options.map((o) => [o.value, o.label])),
    [options]
  )

  return (
    <Combobox
      multiple
      values={value}
      onValuesChange={handleValuesChange}
      disabled={disabled}
    >
      <MultiSelectField
        options={options}
        value={value}
        onChange={onChange}
        onValuesChange={handleValuesChange}
        labelMap={labelMap}
        placeholder={placeholder}
        className={className}
        href={href}
      />
    </Combobox>
  )
}

/**
 * The chips field and the option list. Split out from `MultiSelect` so it can
 * read the combobox's search text and filter the options itself — cmdk only
 * filters for a `ComboboxInput`, which the chip variant does not render.
 */
function MultiSelectField({
  options,
  value,
  onChange,
  onValuesChange,
  labelMap,
  placeholder,
  className,
  href,
}: {
  options: MultiSelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  onValuesChange: (value: string[]) => void
  labelMap: Record<string, string>
  placeholder: string
  className?: string
  href?: (value: string) => string
}) {
  const { inputValue, setInputValue } = useComboboxContext()

  const query = inputValue.trim().toLowerCase()
  const visible = query
    ? options.filter((o) => o.label.toLowerCase().includes(query))
    : options

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !inputValue && value.length) {
      onChange(value.slice(0, -1))
      return
    }
    if (event.key === 'Enter' && visible.length) {
      event.preventDefault()
      const first = visible[0].value
      onValuesChange(
        value.includes(first) ? value.filter((v) => v !== first) : [...value, first]
      )
      setInputValue('')
    }
  }

  return (
    <>
      <ComboboxChips className={cn('w-full', className)}>
        {value.map((v) => (
          <ComboboxChip key={v} onRemove={() => onChange(value.filter((val) => val !== v))}>
            {href ? (
              // A click on the chips opens the list, so the link keeps its click to itself.
              <Link
                to={href(v)}
                className="text-(--ad-link)"
                onClick={(event) => event.stopPropagation()}
              >
                {labelMap[v] ?? v}
              </Link>
            ) : (
              (labelMap[v] ?? v)
            )}
          </ComboboxChip>
        ))}
        <ComboboxChipsInput
          placeholder={value.length === 0 ? placeholder : ''}
          onKeyDown={handleKeyDown}
        />
      </ComboboxChips>
      {/* Focus stays in the chips input so typing keeps filtering. */}
      <ComboboxContent onOpenAutoFocus={(event) => event.preventDefault()}>
        <ComboboxList>
          {visible.length === 0 && (
            // Not ComboboxEmpty: that renders only when cmdk's own filter runs,
            // and the chip variant filters the options itself.
            <div className="py-2 text-center text-sm text-muted-foreground">
              No results found.
            </div>
          )}
          {visible.map((opt) => (
            <ComboboxItem key={opt.value} value={opt.value}>
              {opt.label}
            </ComboboxItem>
          ))}
        </ComboboxList>
      </ComboboxContent>
    </>
  )
}
